<script setup lang="ts">
import { datadogLogs } from "@datadog/browser-logs";
import type { AdvisorCoaches } from "@profile-service-types/index.ts";
import { debounce } from "lodash";
import Button from "primevue/button";
import Column from "primevue/column";
import DataTable from "primevue/datatable";
import Dialog from "primevue/dialog";
import ProgressSpinner from "primevue/progressspinner";
import Steps from "primevue/steps";
import { useToast } from "primevue/usetoast";
import { computed, ref, toRefs, unref, watch } from "vue";
import { useGetAdvisingAndCoaching } from "@/providers/profiles/useGetAdvisingAndCoaching";
import { useGetAdvisorCoaches } from "@/providers/profiles/useGetAdvisorCoaches";
import { usePatchProfileAdvisingAndCoaching } from "@/providers/profiles/usePatchProfileAdvisingAndCoaching";
import AfterEditCell from "./AfterEditCell.vue";
import { steps, UNASSIGNED } from "./constants.ts";
import {
  backButtonPt,
  cancelButtonPt,
  datatablePt,
  dialogPt,
  nextButtonPt,
  progressSpinnerPt,
  stepsPt,
} from "./style.ts";
import type {
  AdvisingAndCoachingInDBWithOrdinal,
  AdvisingAndCoachingPatchWithOrdinal,
  AdvisorGroupRow,
  BeforeEditAdvisor,
  CoachOption,
  Props,
  ResultScreen,
} from "./types.ts";
import { getConflictingOrdinals } from "./utils.ts";

defineOptions({
  name: "myStudentsBulkEditModal",
});

const props = withDefaults(defineProps<Props>(), {
  modalVisible: false,
});

const emit = defineEmits();

const { data, modalVisible } = toRefs(props);

const step = ref<1 | 2>(1); // 1 = edit, 2 = review (read-only)

const goToReview = () => {
  step.value = 2;
};
const goBackToEdit = () => {
  step.value = 1;
};

const toast = useToast();

const q = ref("");

const advisorsAfterEditByKey = ref<Record<string, CoachOption[]>>({});

const participantsWithChangesCount = ref(0);
const participantsWithNoChangesCountRef = ref(0);

const submitResult = ref<ResultScreen>();

const participant_ids = computed(() => {
  const items = data.value ?? [];
  return items.map((item) => item?.student?.participant_id).filter((id) => id !== undefined);
});

const { data: advisingAndCoachingData } = useGetAdvisorCoaches({
  q,
  useQueryOptions: {
    enabled: computed(() => modalVisible.value),
  },
});

const { data: existingAdvisors } = useGetAdvisingAndCoaching<AdvisingAndCoachingInDBWithOrdinal[]>({
  participant_okta_ids: participant_ids,
  useQueryOptions: {
    enabled: computed(() => !!participant_ids.value),
  },
});

const { mutate: executePatchProfileAdvisingAndCoaching, isPending: isPatchProfileAdvisingAndCoachingLoading } =
  usePatchProfileAdvisingAndCoaching();

const getRowKey = (row: AdvisorGroupRow) => {
  return row.row_key ?? UNASSIGNED;
};

const setQDebounced = debounce((value: string) => {
  q.value = value;
}, 300);

const updateModalVisibility = (value) => {
  if (!value) {
    emit("hide");
  }
};

const formatCoachName = (profile) => {
  const first = profile?.preferred_first_name ?? profile?.legal_first_name ?? "";
  const last = profile?.last_name ?? "";
  return [first, last].join(" ").trim() || "";
};

const getCoachSetKey = (coachOktaIds: string[]) => {
  if (coachOktaIds.length === 0) {
    return UNASSIGNED;
  } else {
    return coachOktaIds.slice().sort().join("|");
  }
};

const setAdvisorsAfterEdit = (key: string, value: CoachOption[]) => {
  advisorsAfterEditByKey.value = {
    ...advisorsAfterEditByKey.value,
    [key]: value ?? [],
  };
};

const addAdvisor = (key: string, advisor: CoachOption): void => {
  const current = advisorsAfterEditByKey.value[key] ?? [];
  if (current.some((existing) => existing.okta_id === advisor.okta_id)) {
    return;
  }
  setAdvisorsAfterEdit(key, [...current, { ...advisor, ordinal: undefined }]);
};

const reorderAdvisors = (key: string, advisors: CoachOption[]): void => {
  setAdvisorsAfterEdit(key, advisors);
};

const removeAdvisor = (key: string, index: number): void => {
  const current = advisorsAfterEditByKey.value[key] ?? [];
  setAdvisorsAfterEdit(
    key,
    current.filter((_, i) => i !== index),
  );
};

const updateAdvisorOrdinal = (key: string, index: number, value: number | undefined): void => {
  const current = advisorsAfterEditByKey.value[key] ?? [];
  setAdvisorsAfterEdit(
    key,
    current.map((advisor, i) => (i === index ? { ...advisor, ordinal: value } : advisor)),
  );
};

const getCoachOktaId = (c: { okta_id?: string } | undefined) => c?.okta_id;

const advisingAndCoachingDataWithDisplayName = computed(() => {
  const list: AdvisorCoaches[] = advisingAndCoachingData.value ?? [];
  return list.map((item) => ({
    ...item,
    displayName: formatCoachName(item) || "—",
  }));
});

const groupedByAdvisorRows = computed<AdvisorGroupRow[]>(() => {
  const advisingAndCoachingRecords: AdvisingAndCoachingInDBWithOrdinal[] = existingAdvisors.value ?? [];
  const selectedIds = participant_ids.value ?? [];

  const participantToCoaches = new Map<string, { coach_okta_ids: string[]; before_edit: BeforeEditAdvisor[] }>();

  for (const record of advisingAndCoachingRecords) {
    const participantId = record.participant_okta_id;
    const coachId = record.coach_okta_id;
    const coachProfile = record.coach_profile;

    if (participantId && coachId && coachProfile) {
      if (!participantToCoaches.has(participantId)) {
        participantToCoaches.set(participantId, { coach_okta_ids: [], before_edit: [] });
      }
      const entry = participantToCoaches.get(participantId);

      if (entry && !entry.coach_okta_ids.includes(coachId)) {
        entry.coach_okta_ids.push(coachId);
        entry.before_edit.push({ ...coachProfile, ordinal: record.ordinal });
      }
    }
  }
  const byCoachSet = new Map<string, { participant_okta_ids: string[]; before_edit: BeforeEditAdvisor[] }>();

  for (const pid of selectedIds) {
    const { coach_okta_ids, before_edit } = participantToCoaches.get(pid) ?? {
      coach_okta_ids: [],
      before_edit: [],
    };
    const key = getCoachSetKey(coach_okta_ids);

    if (!byCoachSet.has(key)) {
      byCoachSet.set(key, { participant_okta_ids: [], before_edit });
    }
    byCoachSet.get(key)?.participant_okta_ids.push(pid);
  }

  const rows: AdvisorGroupRow[] = [];
  for (const [row_key, { participant_okta_ids, before_edit }] of byCoachSet) {
    rows.push({
      row_key,
      participant_okta_ids,
      studentCount: participant_okta_ids.length,
      coach_profiles: before_edit,
      before_edit,
      advisorsAfterEdit: [],
    });
  }
  return rows;
});

const afterEditAdvisorsForRow = (row: AdvisorGroupRow): CoachOption[] =>
  advisorsAfterEditByKey.value[getRowKey(row)] ?? [];

// Rule 1: no two advisors in a group may share a contact order.
const hasAnyConflict = computed<boolean>(() =>
  groupedByAdvisorRows.value.some((row) => getConflictingOrdinals(afterEditAdvisorsForRow(row)).size > 0),
);

// Rule 2: every assigned advisor needs a contact order >= 1.
const hasIncompleteOrdinal = computed<boolean>(() =>
  groupedByAdvisorRows.value.some((row) =>
    afterEditAdvisorsForRow(row).some((advisor) => typeof advisor.ordinal !== "number" || advisor.ordinal < 1),
  ),
);

const isNextDisabled = computed<boolean>(() => hasAnyConflict.value || hasIncompleteOrdinal.value);

const onSubmit = () => {
  const payload = buildPatchPayload();
  const participantIds = participant_ids.value ?? [];
  const participantsWithChanges = new Set(payload.map((p) => p.participant_okta_id));
  const participantsWithNoChangesCount = participantIds.length - participantsWithChanges.size;
  if (payload.length === 0) {
    participantsWithChangesCount.value = participantIds.length;
    submitResult.value = {
      successCount: participantIds.length,
      failCount: 0,
      failedUsers: [],
    };
    return;
  }

  participantsWithChangesCount.value = participantIds.length;
  participantsWithNoChangesCountRef.value = participantsWithNoChangesCount;

  executePatchProfileAdvisingAndCoaching(
    { payload },
    {
      onSuccess: (response) => {
        const successfulUsers = response?.successful_users ?? [];
        const successCount = new Set(successfulUsers.map((u) => u.participant_okta_id)).size;

        const failedUsers = response?.failed_users ?? [];
        const failCount = new Set(failedUsers.map((u) => u.okta_id)).size;

        submitResult.value = {
          successCount: successCount + participantsWithNoChangesCountRef.value,
          failCount: failCount,
          failedUsers: failedUsers,
        };
      },
      onError: (error, variables) => {
        datadogLogs.logger.warn("Failed to update Advising and Coaching records.", {
          error: error.message,
          payload: unref(variables.payload),
        });
        toast.add({
          severity: "warn",
          summary: "Bulk Edit",
          detail: "Failed to update Advising and Coaching records.",
          group: "br",
          life: 5000,
        });
      },
    },
  );
};

const buildPatchPayload = (): AdvisingAndCoachingPatchWithOrdinal[] => {
  const result: AdvisingAndCoachingPatchWithOrdinal[] = [];
  const rows = groupedByAdvisorRows.value;
  const state = advisorsAfterEditByKey.value;

  for (const row of rows) {
    const key = getRowKey(row);
    const afterCoaches = state[key] ?? [];
    const newCoachOktaIds = afterCoaches
      .map((c) => getCoachOktaId(c))
      .filter((id): id is string => id !== undefined && id !== "");

    const beforeProfiles = row.coach_profiles ?? [];
    const oldCoachOktaIds = beforeProfiles
      .map((p) => getCoachOktaId(p as { okta_id?: string }))
      .filter((id): id is string => id !== undefined && id !== "");

    const oldSet = new Set(oldCoachOktaIds);
    const newSet = new Set(newCoachOktaIds);

    const afterOrdinalById = new Map<string, number | undefined>(
      afterCoaches.map((c) => [getCoachOktaId(c) ?? "", c.ordinal]),
    );
    const beforeOrdinalById = new Map<string, number | undefined>(
      (row.before_edit ?? []).map((p) => [getCoachOktaId(p) ?? "", p.ordinal]),
    );

    for (const participant_okta_id of row.participant_okta_ids) {
      for (const oldId of oldCoachOktaIds) {
        if (!newSet.has(oldId)) {
          result.push({
            participant_okta_id,
            old_coach_okta_id: oldId,
            new_coach_okta_id: undefined,
          });
        }
      }
      for (const newId of newCoachOktaIds) {
        if (!oldSet.has(newId)) {
          result.push({
            participant_okta_id,
            old_coach_okta_id: undefined,
            new_coach_okta_id: newId,
            ordinal: afterOrdinalById.get(newId),
          });
        } else if (afterOrdinalById.get(newId) !== beforeOrdinalById.get(newId)) {
          result.push({
            participant_okta_id,
            old_coach_okta_id: newId,
            new_coach_okta_id: newId,
            ordinal: afterOrdinalById.get(newId),
          });
        }
      }
    }
  }

  return result;
};

watch(modalVisible, (visible) => {
  if (!visible) {
    step.value = 1;
    submitResult.value = undefined;
    participantsWithChangesCount.value = 0;
  }
});

watch(
  groupedByAdvisorRows,
  (rows) => {
    const state = advisorsAfterEditByKey.value;
    for (const row of rows) {
      const key = getRowKey(row);
      state[key] = (row.before_edit ?? []).map((p) => ({
        ...p,
        displayName: formatCoachName(p),
      }));
    }
  },
  { immediate: true },
);

const onFilter = (event: { value: string }) => {
  setQDebounced(event.value ?? "");
};
</script>

<template>
  <Dialog :visible="modalVisible" @update:visible="updateModalVisibility" modal :dismissable-mask="true" :close-on-escape="true" :pt="dialogPt">
    <template #header>
      <div class="inline-flex align-items-center justify-content-center">
        <div>Bulk Actions for {{ data.length }} Students</div>
      </div>
    </template>
    <template #default>
      <div v-if="!isPatchProfileAdvisingAndCoachingLoading && !submitResult">
        <Steps
          :model="steps"
          :activeStep="step - 1"
          class="p-3"
          :pt="stepsPt(step)"
        />
        <div v-if="step === 1" class="inline-flex items-center gap-2 mx-3 mb-3 px-3 py-2 rounded-md border border-teal-50 bg-teal-light text-teal-900 text-xs">
          <span class="material-symbols-outlined text-sm">info</span>
          <span>Contact order ranks advisors (1 = primary). Each advisor in a group must have a unique number.</span>
        </div>
        <div
          v-if="step === 1 && hasAnyConflict"
          class="flex items-center gap-2 mx-3 mb-3 px-3 py-2 rounded-md border border-red-300 bg-red-light text-xs text-red-700"
        >
          <span class="material-symbols-outlined text-sm">error</span>
          Duplicate contact order values detected. Each advisor must have a unique number within their student group.
        </div>
        <DataTable :value="groupedByAdvisorRows" :pt="datatablePt">
          <Column
            header="# of Students"
            headerClass="w-20 text-right"
            bodyClass="w-20 text-right"
          >
              <template #body="slotProps">
              {{ slotProps.data?.studentCount}}
            </template>
          </Column>
          <Column
            header="Advisor(s) Before Edit"
            headerClass="w-[200px]"
            bodyClass="w-[200px]"
          >
            <template #body="slotProps">
              <template v-if="slotProps.data?.before_edit?.length">
                <div
                  v-for="advisor in slotProps.data.before_edit"
                  :key="advisor.okta_id"
                  class="flex items-center gap-2 py-0.5"
                >
                  <span>{{ formatCoachName(advisor) }}</span>
                  <span
                    v-if="typeof advisor.ordinal === 'number'"
                    class="inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-medium bg-teal-accent text-teal-900"
                  >#{{ advisor.ordinal }}</span>
                </div>
              </template>
              <span v-else class="italic text-gray-500">No advisor</span>
            </template>
          </Column>
          <Column header="Advisor(s) After Edit">
            <template #body="slotProps">
              <template v-if="step === 1">
                <AfterEditCell
                  :advisors="advisorsAfterEditByKey[getRowKey(slotProps.data)] ?? []"
                  :options="advisingAndCoachingDataWithDisplayName"
                  @add="(advisor) => addAdvisor(getRowKey(slotProps.data), advisor)"
                  @remove="(index) => removeAdvisor(getRowKey(slotProps.data), index)"
                  @update:ordinal="(index, value) => updateAdvisorOrdinal(getRowKey(slotProps.data), index, value)"
                  @reorder="(advisors) => reorderAdvisors(getRowKey(slotProps.data), advisors)"
                  @filter="onFilter"
                />
              </template>
              <template v-else>
                <div
                  v-for="advisor in advisorsAfterEditByKey[getRowKey(slotProps.data)] ?? []"
                  :key="advisor.okta_id"
                  class="flex items-center gap-2 py-0.5"
                >
                  <span>{{ formatCoachName(advisor) }}</span>
                  <span
                    v-if="typeof advisor.ordinal === 'number'"
                    class="inline-flex items-center justify-center rounded px-1.5 py-0.5 text-xs font-medium bg-teal-accent text-teal-900"
                  >#{{ advisor.ordinal }}</span>
                </div>
              </template>
            </template>
          </Column>
        </DataTable>
      </div>
      <template v-else-if="submitResult">
        <div class="flex flex-row gap-4 p-4 justify-center" v-if="submitResult.successCount">
          <div class="relative inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#11B981]">
            <span class="material-symbols-outlined text-white text-xl">check</span>
          </div>
          <p class="text-lg text-indigo-base">Updates for {{ submitResult.successCount }} of {{ participantsWithChangesCount }} students have been made successfully.</p>
        </div>
        <div class="flex flex-row gap-4 p-4 px-4 justify-center" v-if="submitResult.failCount > 0">
          <div class="relative inline-flex items-center justify-center w-6 h-6 rounded-full text-red border-2 border-red-700">
            <span class="material-symbols-outlined text-red-700">exclamation</span>
          </div>
          <div class="flex flex-col">
            <p class="text-lg text-indigo-base">Updates for {{ submitResult.failCount }} of {{ participantsWithChangesCount }} students have NOT been made successfully. They are:</p>
            <div class="pt-6">
              <p v-for="failedUser in submitResult.failedUsers" class="flex justify-center text-center">
              <div class="flex flex-row text-lg text-indigo-base">
                {{ failedUser.first_name }} {{ failedUser.last_name }}
              </div>
            </p>
            </div>
          </div>
        </div>
      </template>
      <div v-else class="flex relative items-center justify-center">
        <div class="flex flex-col justify-center items-center">
          <div class="text-indigo-base">
            Updates will take a moment.
          </div>
          <div class="pt-4">
            <ProgressSpinner :pt="progressSpinnerPt"/>
          </div>
        </div>
      </div>
    </template>
    <template #footer v-if="!submitResult">
      <div class="flex flex-row justify-between w-full gap-2 h-10 pt-2" v-if="!isPatchProfileAdvisingAndCoachingLoading">
        <div class="flex gap-2">
          <Button v-if="step === 2" @click="goBackToEdit" :pt="backButtonPt" class="flex flex-row gap-1">
            <span class="material-symbols-outlined">arrow_left_alt</span>
            Back
          </Button>
        </div>
        <div class="flex flex-row gap-2">
          <Button label="Cancel" @click="updateModalVisibility(false)" :pt="cancelButtonPt"/>
          <Button
            v-if="step === 1"
            :disabled="isNextDisabled"
            @click="goToReview"
            :pt="nextButtonPt"
            class="flex flex-row gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
            <span class="material-symbols-outlined">arrow_right_alt</span>
          </Button>
          <Button v-if="step === 2" label="Confirm & Submit" @click="onSubmit" :pt="nextButtonPt"/>
        </div>
      </div>
    </template>
  </Dialog>
</template>
